<template>
  <div class="pa-4 fill-height grid-container">
    <div class="side-bar">
      <v-card-title class="pa-0 grow-false d-flex flex-row align-center">
        <div>
          {{ asset.plate || asset.boxMacAddress || asset.serial }}
          <span class="mx-1"> - </span>
          <span class="primary--text text--lighten-2">
            {{ asset.brand }}
            {{ asset.model }}
          </span>
        </div>
      </v-card-title>

      <div class="grow-false d-flex align-center" style="gap: 1em">
        <v-dialog v-model="pickerDialog" width="unset" persistent scrollable>
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-bind:title="$t('calendarRange')"
              v-bind:value="
                dateFormat(dateRange.startDate) +
                ' - ' +
                dateFormat(dateRange.endDate)
              "
              v-bind="attrs"
              v-on="on"
              class="grow-true mt-4 mb-2"
              prepend-inner-icon="mdi-calendar"
              dense
              hide-details
              outlined
              readonly
            />
          </template>

          <v-card class="pa-2">
            <v-card-text>
              <date-range-picker
                ref="picker"
                v-model="dateRange"
                v-bind:locale-data="{
                  firstDay: 1,
                  format: `${$t('tzLocale').toLowerCase()} HH:mm`,
                  daysOfWeek: momentTranslations.days,
                  monthNames: momentTranslations.months,
                }"
                class="mt-2 range-picker"
                opens="inline"
                linkedCalendars
                showDropdowns
                timePicker
                timePicker24Hour
              >
                <template v-slot:input="picker">
                  <div class="d-flex flex-column align-center no-wrap">
                    <v-card-subtitle>
                      {{ $t("calendarRange") }}:
                      <span class="font-weight-bold body-1">
                        {{ dateFormat(picker.startDate) }}
                      </span>
                      -
                      <span class="font-weight-bold body-1">
                        {{ dateFormat(picker.endDate) }}
                      </span>
                    </v-card-subtitle>
                  </div>
                </template>
                <div slot="footer" slot-scope="data" class="slot">
                  <v-card-actions class="py-0">
                    <v-spacer />
                    <v-btn v-on:click="pickerDialog = false" color="error" text>
                      {{ $t("btnCancel") }}
                    </v-btn>
                    <v-btn
                      v-bind:disabled="loadingChart || data.in_selection"
                      v-on:click="updateDatesRange(data)"
                      color="primary"
                      text
                    >
                      {{ $t("btnSave") }}
                    </v-btn>
                  </v-card-actions>
                </div>
              </date-range-picker>
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>

      <v-slider
        v-model="pointsAmount"
        v-on:change="fetchPredictions"
        v-bind:disabled="loadingChart"
        v-bind:label="$t('forecastPointsAmount')"
        v-bind:min="1"
        v-bind:max="10"
        class="grow-false mt-2 mb-6"
        thumb-label
        ticks
        dense
        hide-details
      />

      <v-card-subtitle class="pa-0 grow-false font-weight-bold">
        {{ $t("forecastMeasures") }}
      </v-card-subtitle>

      <div class="grow-true main-list">
        <v-radio-group v-model="selectedKey" class="ma-0 pa-0" hide-details>
          <v-list
            v-for="group in groupedMeasures"
            v-bind:key="'group-' + group.group"
            class="py-0"
            dense
          >
            <v-list-group v-bind:value="true" class="forecast-group" no-action>
              <template v-slot:activator>
                <v-list-item-title class="text-subtitle-1 black--text">
                  {{ group.group }}
                </v-list-item-title>
              </template>

              <v-list-item
                v-for="measure in group.measures"
                v-bind:key="measure.key"
                class="pl-6 pr-0"
              >
                <v-radio
                  v-bind:value="measure.key"
                  v-bind:disabled="loadingChart"
                  class="ma-0"
                >
                  <template v-slot:label>
                    <span>
                      {{ measure.name }}
                      <span v-if="measure.unit" class="caption">
                        ({{ measure.unit }})
                      </span>
                      <span
                        v-if="measure.type !== 'Numeric'"
                        class="caption ms-1"
                      >
                        [{{ measure.type }}]
                      </span>
                    </span>
                  </template>
                </v-radio>
              </v-list-item>
            </v-list-group>
          </v-list>
        </v-radio-group>

        <div v-if="fetched && matrices.length === 0" class="pa-4 text-center">
          {{ $t("forecastNoData") }}
        </div>
      </div>
    </div>

    <div class="plot-area">
      <div class="grow-false d-flex align-stretch mt-1 mb-2" style="gap: 1em">
        <div class="grow-true d-flex flex-column" style="gap: 0.5em">
          <v-card
            v-if="selectedMeasure"
            class="d-flex align-center px-3 py-2"
            style="gap: 0.5em"
            flat
            outlined
          >
            <span class="text-subtitle-1 font-weight-bold">
              {{ selectedMeasure.group }}
            </span>
            <span class="caption"> / </span>
            <span class="text-subtitle-1 font-weight-bold primary--text">
              {{ selectedMeasure.name }}
            </span>
          </v-card>

          <v-alert v-else class="ma-0" type="warning" dense text>
            {{ $t("forecastComputedHint") }}
          </v-alert>

          <v-select
            v-model="selectedComputed"
            v-bind:items="computedSelectItems"
            v-bind:label="$t('forecastComputedRun')"
            v-bind:disabled="overlayRuns || !selectedKey"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
          />
        </div>

        <v-card
          class="grow-false d-flex flex-column justify-center px-6 py-1"
          flat
          outlined
        >
          <v-switch
            v-model="startAtZero"
            v-bind:disabled="loadingChart"
            v-bind:label="$t('axesFromZeroBtn')"
            class="text-no-wrap pa-0 my-2"
            hide-details
            inset
          />

          <v-switch
            v-model="overlayRuns"
            v-bind:disabled="!selectedKey"
            v-bind:label="$t('forecastOverlayRuns')"
            class="text-no-wrap pa-0 my-2"
            hide-details
            inset
          />
        </v-card>
      </div>

      <div v-show="!selectedMeasure || isNumericSelected" class="plot rounded">
        <div class="plot-chart">
          <Chart
            v-bind:chart-data="chartData"
            v-bind:filter="plotKeys"
            v-bind:colors="plotColors"
            v-bind:asset="asset.serial"
            v-bind:min-timestamp="chartStarting"
            v-bind:max-timestamp="chartEnding"
            v-bind:start-at-zero="startAtZero"
            v-on:data-loaded="loadingChart = false"
            class="pa-2"
            topic-prefix="predictions/"
            ref="lineChart"
          />
        </div>

        <div
          v-if="overlayRuns"
          class="grow-false d-flex flex-wrap px-2 pb-2"
          style="gap: 1em"
        >
          <div
            v-for="entry in overlayLegend"
            v-bind:key="entry.label"
            class="d-flex align-center caption"
          >
            <span
              v-bind:style="{ backgroundColor: entry.color }"
              class="legend-swatch me-1"
            />
            {{ entry.label }}
          </div>
        </div>
      </div>

      <div
        v-if="selectedMeasure && !isNumericSelected"
        class="plot-data rounded"
      >
        <div class="plot-data-title">
          <v-card-title class="py-0">
            {{ $t("forecastValuesTable") }}
          </v-card-title>
          <v-divider />
        </div>

        <div class="plot-data-list">
          <v-data-table
            v-if="tableRows.length > 0"
            v-bind:headers="tableHeaders"
            v-bind:items="tableRows"
            v-bind:items-per-page="-1"
            item-key="measure"
            hide-default-footer
          >
            <template v-slot:item.targetDate="{ item }">
              {{ dateFormat(item.targetDate) }}
            </template>
            <template v-slot:item.computedDate="{ item }">
              {{ dateFormat(item.computedDate) }}
            </template>
          </v-data-table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.grid-container {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: minmax(min-content, 32%) auto;
  grid-template-rows: minmax(0, 90vh);
  grid-template-areas: "side plot";
}

.side-bar {
  grid-area: side;
  display: flex;
  gap: 0.1rem;
  flex-direction: column;
  overflow: hidden;
}

.grow-false {
  flex: 0 1 0;
}

.grow-true {
  flex: 1 1 0;
}

.main-list {
  overflow: auto;
}

/* Group header background, matching the normal measures list. */
.forecast-group >>> .v-list-group__header {
  background-color: #e8eaf6; /* indigo lighten-5 */
}

/* Keep switch labels on a single line. */
.text-no-wrap >>> .v-label {
  white-space: nowrap;
}

.plot-area {
  grid-area: plot;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.plot {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #dfe7f0;
  overflow: hidden;
}

.plot-chart {
  flex: 1 1 0;
  position: relative;
  overflow: hidden;
}

.legend-swatch {
  display: inline-block;
  width: 0.8em;
  height: 0.8em;
  border-radius: 50%;
}

.plot-data {
  flex: 1 1 0;
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-rows: min-content 1fr;
  border: 1px solid #dfe7f0;
  overflow: hidden;
}

.plot-data-list {
  height: 100%;
  overflow: auto;
}
</style>

<script>
import ApiRequests from "../utils/requests";
import Pages from "../utils/pages";
import Messages from "../utils/messages";
import AssetInfo from "../models/AssetInfo";
import AssetPredictionsRequestBody from "../models/AssetPredictionsRequestBody";
import Chart from "../components/Chart";
import moment from "moment";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import distinctColors from "distinct-colors";
import {
  predictionsToChartData,
  predictionsToOverlay,
  computedDates,
  nonNumericRows,
} from "../utils/predictions";

// Fixed chart color while only one measure can be plotted at a time.
const INDIGO = "#3F51B5";

export default {
  name: "AssetForecasts",

  components: {
    Chart,
    DateRangePicker,
  },

  data: () => ({
    assetId: undefined,
    asset: new AssetInfo(),
    loadingChart: false,
    startAtZero: true,
    pointsAmount: 3,
    fetched: false,
    matrices: [],
    selectedKey: undefined,
    selectedComputed: undefined,
    overlayRuns: false,
    chartData: {},
    plotColors: {},
    colors: [],
    pickerDialog: false,
    momentTranslations: {
      days: moment.weekdaysShort(false),
      months: moment.months(true),
    },
    dateRange: {
      startDate: moment(),
      endDate: moment().add(7, "days"),
    },
    chartStarting: moment().toDate().getTime(),
    chartEnding: moment().add(7, "days").toDate().getTime(),
  }),

  computed: {
    assetDbId() {
      return this.$route.params.assetId;
    },
    availableMeasures() {
      return this.matrices.map((m) => ({
        key: `${m.group}/${m.name}`,
        name: m.name,
        group: m.group,
        type: m.type,
        unit: m.unit,
      }));
    },
    selectedMatrices() {
      return this.matrices.filter(
        (m) => `${m.group}/${m.name}` === this.selectedKey
      );
    },
    // The currently selected measure (group/name/unit/type), or undefined.
    selectedMeasure() {
      return this.availableMeasures.find((m) => m.key === this.selectedKey);
    },
    // Numeric selection => chart; non-numeric => values table (mutually exclusive).
    isNumericSelected() {
      return this.selectedMeasure?.type === "Numeric";
    },
    // Topic keys currently plotted, matching Chart's topic-prefix stripping.
    plotKeys() {
      return Object.keys(this.chartData).map((k) =>
        k.replace("predictions/", "")
      );
    },
    // Color legend for the overlay: one entry per plotted computed run.
    overlayLegend() {
      return Object.keys(this.chartData).map((label) => ({
        label,
        color: this.plotColors[label],
      }));
    },
    // Available measures grouped by their "group" field, for the selection list.
    groupedMeasures() {
      const groups = {};
      this.availableMeasures.forEach((measure) => {
        (groups[measure.group] ??= []).push(measure);
      });
      return Object.keys(groups)
        .sort()
        .map((group) => ({ group, measures: groups[group] }));
    },
    computedRuns() {
      return computedDates(this.selectedMatrices);
    },
    computedSelectItems() {
      return this.computedRuns.map((run) => ({
        text: this.dateFormat(run),
        value: run,
      }));
    },
    tableRows() {
      return nonNumericRows(this.selectedMatrices, this.selectedComputed);
    },
    tableHeaders() {
      return [
        { text: this.$t("detailsTitleName"), value: "measure" },
        { text: this.$t("forecastTargetDate"), value: "targetDate" },
        { text: this.$t("detailsTitleValue"), value: "value" },
        { text: this.$t("detailsTitleTs"), value: "computedDate" },
      ];
    },
  },

  async beforeMount() {
    try {
      await this.fetchAssetData();
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error(err);
      this.$router.replace({ path: "/dashboard" });
      this.$bus.$emit(Messages.ERROR_MSG, {
        error: err?.response?.data?.error || err,
        description: this.$t("assetFetchFailure").replace(
          "%serial%",
          this.assetId
        ),
      });
      return;
    }

    this.colors = distinctColors({ count: 20 }).map((cc) => cc.hex("rgb"));
    this.$store.commit("setCurrentPage", Pages.FORECASTS_PAGE);
    this.$store.commit("setCustomCurrentPage", -1);
    this.fetchPredictions();
  },

  mounted() {
    this.$store.commit("addPage", {
      text: this.$t("btnShowForecasts"),
      to: `/asset-forecasts/${this.assetDbId}`,
      root: false,
    });
  },

  watch: {
    selectedKey(newKey) {
      // Preselect the first (latest) computed run when a measure is selected.
      this.selectedComputed = newKey ? this.computedRuns[0] : undefined;
      this.rebuildChart();
    },
    selectedComputed() {
      this.rebuildChart();
    },
    overlayRuns() {
      this.rebuildChart();
    },
  },

  methods: {
    async fetchAssetData() {
      this.asset = new AssetInfo(
        (await ApiRequests.getAsset(this.assetDbId, undefined, undefined)).data
      );

      this.assetId = this.asset.serial;
    },
    fetchPredictions() {
      this.loadingChart = true;

      const body = new AssetPredictionsRequestBody();

      body.ids = [this.assetDbId];
      body.paths = [];
      body.starting = moment(this.dateRange.startDate).format();
      body.ending = moment(this.dateRange.endDate).format();
      body.pointsAmount = this.pointsAmount;

      ApiRequests.getAssetPredictions(
        body,
        (res) => {
          this.matrices = res.data[this.assetDbId] ?? [];
          this.fetched = true;

          this.rebuildChart();
        },
        (err) => {
          this.loadingChart = false;
          this.fetched = true;

          if (process.env.NODE_ENV === "development") console.error(err);
        }
      );
    },
    rebuildChart() {
      // One measure at a time: label each series by its computed-run date
      // (shown in the chart popup) instead of the measure group/name.
      // ponytail: collides if multiple measures share a run; fine while single-select.
      const raw = this.overlayRuns
        ? predictionsToOverlay(
            this.selectedMatrices,
            this.computedRuns.slice(0, 4)
          )
        : predictionsToChartData(this.selectedMatrices, this.selectedComputed);

      this.chartData = {};
      Object.keys(raw).forEach((key) => {
        // Overlay keys look like "group/name @<run>"; otherwise use selectedComputed.
        const run = key.includes(" @")
          ? key.split(" @")[1]
          : this.selectedComputed;
        this.chartData[
          `${this.$t("forecastComputedAt")}${this.dateFormat(run)}`
        ] = raw[key];
      });

      const keys = Object.keys(this.chartData);

      // Single series (the current one-measure limit): always indigo.
      // Multiple series stay wired to distinct colors for future multi-select.
      this.plotColors = {};
      keys.forEach((k, i) => {
        this.plotColors[k] = keys.length === 1 ? INDIGO : this.colors[i];
      });
    },
    updateDatesRange(data) {
      data.clickApply();
      this.pickerDialog = false;

      this.chartStarting = moment(this.dateRange.startDate).toDate().getTime();
      this.chartEnding = moment(this.dateRange.endDate).toDate().getTime();

      this.fetchPredictions();
    },
    dateFormat(date) {
      if (date == undefined) return "";

      return moment(date).format(`${this.$t("tzLocale")} HH:mm`);
    },
  },
};
</script>
